import React, { Component, ErrorInfo, ReactNode } from 'react';
import { captureRouteChunkErrorForAudit, isLoadingAuditEnabled } from '@/lib/loadingBootAudit';

interface RouteChunkErrorBoundaryProps {
  children: ReactNode;
}

interface RouteChunkErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches errors from lazy route chunk load failures (e.g. 404, network error, HTML instead of JS).
 * Shows "Có bản mới, bấm tải lại" + reload button instead of infinite loading.
 */
function isChunkLoadError(error: Error): boolean {
  const msg = error?.message ?? '';
  return (
    msg.includes('Loading chunk') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Unexpected token <') // HTML returned instead of JS (e.g. SPA rewrite misconfig)
  );
}

const CHUNK_AUTO_RELOAD_DELAY_MS = 800;

export class RouteChunkErrorBoundary extends Component<
  RouteChunkErrorBoundaryProps,
  RouteChunkErrorBoundaryState
> {
  private autoReloadScheduledRef = { current: false };

  constructor(props: RouteChunkErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): RouteChunkErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    captureRouteChunkErrorForAudit(error, errorInfo);
    if (import.meta.env.DEV) {
      console.error('[RouteChunkErrorBoundary]', error.message, errorInfo.componentStack);
    }
    // Optional: auto reload once after delay if chunk load fail (e.g. cache mismatch)
    if (isChunkLoadError(error) && !this.autoReloadScheduledRef.current && !isLoadingAuditEnabled()) {
      this.autoReloadScheduledRef.current = true;
      window.setTimeout(() => {
        window.location.reload();
      }, CHUNK_AUTO_RELOAD_DELAY_MS);
    }
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const isChunk = isChunkLoadError(this.state.error);
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-4">
          <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-muted/30 p-6 text-center">
            <p className="text-sm font-medium text-foreground">
              {isChunk ? 'Có bản mới hoặc lỗi tải trang. Vui lòng tải lại.' : 'Đã xảy ra lỗi.'}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Tải lại
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
